import React, { useState, useContext, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import InputAdornment from "@material-ui/core/InputAdornment";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import EmailOutlinedIcon from "@material-ui/icons/EmailOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ForumIcon from "@material-ui/icons/Forum";
import SecurityIcon from "@material-ui/icons/Security";
import { Helmet } from "react-helmet";
import robotGuide from "../../assets/cchat-robot-guide.png";

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100vw",
		height: "100vh",
		background: theme.mode === "light" ? theme.palette.light : theme.palette.dark,
		backgroundRepeat: "no-repeat",
		backgroundSize: "100% 100%",
		backgroundPosition: "center",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		textAlign: "center",
	},
	paper: {
		backgroundColor: theme.mode === "light" ? "rgba(255, 255, 255, 0.7)" : "rgba(255, 255, 255, 0.2)",
		backdropFilter: "blur(10px)",
		boxShadow: theme.mode === "light" ? "0 4px 6px rgba(0, 0, 0, 0.1)" : "0 4px 6px rgba(255, 255, 255, 0.2)",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: "55px 30px",
		borderRadius: "12.5px",
	},
	avatar: {
		margin: theme.spacing(1),
		backgroundColor: theme.palette.primary.main,
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(1),
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
	},
	powered: {
		color: "white",
	},
	logoBox: {
		width: "100%",
		height: 96,
		marginBottom: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	logoImg: {
		width: "265px",
		maxWidth: "78%",
		height: "auto",
		maxHeight: "92px",
		margin: "0 auto",
		objectFit: "contain",
		content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")",
	},
	iconButton: {
		position: "absolute",
		top: 10,
		right: 10,
		color: theme.mode === "light" ? "black" : "white",
	},
	model2Root: {
		width: "100vw",
		minHeight: "100vh",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: theme.spacing(4),
		backgroundImage:
			"linear-gradient(90deg, rgba(2, 8, 28, 0.82) 0%, rgba(4, 33, 92, 0.56) 38%, rgba(4, 15, 41, 0.16) 72%, rgba(2, 8, 28, 0.34) 100%), url('/imgs/site/Cchat_imgs/Robozinho_do_Cchat_wallpaper.png')",
		backgroundRepeat: "no-repeat",
		backgroundSize: "cover",
		backgroundPosition: "center center",
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(2),
			alignItems: "flex-start",
			backgroundPosition: "68% center",
		},
	},
	model2Shell: {
		width: "100%",
		maxWidth: 470,
		minHeight: "auto",
		display: "grid",
		gridTemplateColumns: "1fr",
		alignItems: "center",
		overflow: "visible",
		[theme.breakpoints.down("sm")]: {
			gridTemplateColumns: "1fr",
			minHeight: "auto",
		},
	},
	model2BrandPanel: {
		display: "none",
	},
	model2BrandContent: {
		display: "none",
	},
	model2LogoWrap: {
		width: 152,
		height: 76,
		padding: theme.spacing(1.5),
		borderRadius: 18,
		backgroundColor: "rgba(255,255,255,0.94)",
		boxShadow: "0 18px 32px rgba(0, 0, 0, 0.16)",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginBottom: theme.spacing(4),
		[theme.breakpoints.down("xs")]: {
			width: 132,
			height: 66,
			marginBottom: theme.spacing(2.5),
		},
	},
	model2Logo: {
		maxWidth: "100%",
		maxHeight: "100%",
		objectFit: "contain",
		content: "url(" + (theme.mode === "light" ? theme.calculatedLogoLight() : theme.calculatedLogoDark()) + ")",
	},
	model2Eyebrow: {
		margin: 0,
		fontSize: 12,
		fontWeight: 800,
		textTransform: "uppercase",
		letterSpacing: 1.2,
		color: "rgba(255,255,255,0.82)",
	},
	model2Title: {
		margin: theme.spacing(1, 0, 1.5),
		fontSize: 34,
		lineHeight: 1.1,
		fontWeight: 900,
		letterSpacing: 0,
		[theme.breakpoints.down("xs")]: {
			fontSize: 27,
		},
	},
	model2Subtitle: {
		maxWidth: 340,
		margin: 0,
		fontSize: 15,
		lineHeight: 1.55,
		color: "rgba(255,255,255,0.84)",
	},
	model2TrustList: {
		display: "none",
	},
	model2TrustItem: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.25),
		padding: theme.spacing(1.25, 1.5),
		borderRadius: 12,
		border: "1px solid rgba(255,255,255,0.18)",
		backgroundColor: "rgba(255,255,255,0.12)",
		backdropFilter: "blur(8px)",
		fontSize: 13,
		fontWeight: 800,
		"& svg": {
			fontSize: 20,
			color: "#ffffff",
		},
	},
	model2Mascot: {
		display: "none",
	},
	model2FormPanel: {
		position: "relative",
		gridColumn: 1,
		gridRow: 1,
		padding: theme.spacing(5),
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: 24,
		border: "1px solid rgba(191, 219, 254, 0.42)",
		background: "linear-gradient(180deg, rgba(8, 29, 82, 0.84) 0%, rgba(3, 18, 51, 0.76) 100%)",
		backdropFilter: "blur(18px)",
		boxShadow: "0 30px 90px rgba(0, 0, 0, 0.34)",
		overflow: "hidden",
		"&:before": {
			content: '""',
			position: "absolute",
			inset: 0,
			pointerEvents: "none",
			background:
				"radial-gradient(circle at 18% 0%, rgba(255,255,255,0.18), transparent 34%), linear-gradient(135deg, rgba(10,164,221,0.16), transparent 44%)",
		},
		[theme.breakpoints.down("xs")]: {
			padding: theme.spacing(3, 2.25),
			borderRadius: 20,
		},
	},
	model2ThemeButton: {
		display: "none",
	},
	model2FormCard: {
		width: "100%",
		maxWidth: 390,
		position: "relative",
		zIndex: 1,
	},
	model2FormLogoBox: {
		width: 210,
		height: 76,
		marginBottom: theme.spacing(3),
		marginLeft: "auto",
		marginRight: "auto",
		padding: 0,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		[theme.breakpoints.down("xs")]: {
			width: 178,
			height: 64,
			marginBottom: theme.spacing(2.5),
		},
	},
	model2WhiteLogo: {
		display: "block",
		width: "100%",
		height: "100%",
		objectFit: "contain",
		filter: "drop-shadow(0 12px 20px rgba(0, 0, 0, 0.32))",
	},
	model2FormBadge: {
		display: "none",
	},
	model2FormHeader: {
		marginBottom: theme.spacing(3),
		textAlign: "left",
	},
	model2FormTitle: {
		margin: 0,
		color: "#ffffff",
		fontSize: 30,
		lineHeight: 1.18,
		fontWeight: 900,
		letterSpacing: 0,
		[theme.breakpoints.down("xs")]: {
			fontSize: 25,
		},
	},
	model2FormSubtitle: {
		margin: theme.spacing(1.25, 0, 0),
		color: "rgba(226, 239, 255, 0.86)",
		fontSize: 15,
		lineHeight: 1.55,
	},
	model2TextField: {
		marginBottom: theme.spacing(2),
		"& .MuiInputLabel-root": {
			color: "rgba(226, 239, 255, 0.82)",
		},
		"& .MuiInputLabel-root.Mui-focused": {
			color: "#ffffff",
		},
		"& .MuiOutlinedInput-root": {
			borderRadius: 12,
			color: "#ffffff",
			backgroundColor: "rgba(7, 18, 48, 0.58)",
			"& fieldset": {
				borderColor: "rgba(191, 219, 254, 0.44)",
			},
			"&:hover fieldset": {
				borderColor: "rgba(219, 234, 254, 0.78)",
			},
			"&.Mui-focused fieldset": {
				borderWidth: 2,
				borderColor: "#ffffff",
			},
		},
		"& .MuiInputAdornment-root svg": {
			color: "#ffffff",
		},
	},
	model2PasswordToggle: {
		padding: 6,
		color: "#ffffff",
		"&:hover": {
			backgroundColor: "rgba(255, 255, 255, 0.1)",
		},
	},
	model2Submit: {
		height: 52,
		marginTop: theme.spacing(1),
		borderRadius: 12,
		fontWeight: 900,
		textTransform: "none",
		fontSize: 15,
		letterSpacing: 0,
		color: "#ffffff",
		background: "linear-gradient(135deg, #04359A 0%, #075EEA 100%)",
		boxShadow: "0 16px 28px rgba(4, 53, 154, 0.28)",
		"&:hover": {
			background: "linear-gradient(135deg, #032b7d 0%, #064dbf 100%)",
			boxShadow: "0 18px 34px rgba(4, 53, 154, 0.34)",
		},
	},
	model2Links: {
		marginTop: theme.spacing(2),
		display: "flex",
		justifyContent: "center",
		"& a": {
			fontWeight: 700,
			color: "#ffffff",
		},
	},
}));

const Login = () => {
	const classes = useStyles();
	const { colorMode } = useContext(ColorModeContext);
	const { appLogoFavicon, appName, mode } = colorMode;
	const [user, setUser] = useState({ email: "", password: "" });
	const [allowSignup, setAllowSignup] = useState(false);
	const [loginPageModel, setLoginPageModel] = useState("model1");
	const [showPassword, setShowPassword] = useState(false);
	const { getPublicSetting } = useSettings();
	const { handleLogin } = useContext(AuthContext);

	const handleChangeInput = (e) => {
		setUser({ ...user, [e.target.name]: e.target.value });
	};

	const handlSubmit = (e) => {
		e.preventDefault();
		handleLogin(user);
	};

	const handleTogglePassword = () => {
		setShowPassword((current) => !current);
	};

	useEffect(() => {
		getPublicSetting("allowSignup")
			.then((data) => {
				setAllowSignup(data === "enabled");
			})
			.catch((error) => {
				console.log("Error reading setting", error);
			});

		getPublicSetting("loginPageModel")
			.then((data) => {
				setLoginPageModel(data === "model2" ? "model2" : "model1");
			})
			.catch((error) => {
				console.log("Error reading login model setting", error);
			});
	}, []);

	const renderSignupLink = () => (
		allowSignup && (
			<Grid container>
				<Grid item>
					<Link href="#" variant="body2" component={RouterLink} to="/signup">
						{i18n.t("login.buttons.register")}
					</Link>
				</Grid>
			</Grid>
		)
	);

	const renderModel1 = () => (
		<div className={classes.root}>
			<Container component="main" maxWidth="xs">
				<CssBaseline />
				<div className={classes.paper}>
					<IconButton className={classes.iconButton} onClick={colorMode.toggleColorMode}>
						{mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
					</IconButton>
					<div className={classes.logoBox}>
						<img className={classes.logoImg} alt="logo" />
					</div>
					<form className={classes.form} noValidate onSubmit={handlSubmit}>
						<TextField
							variant="outlined"
							margin="normal"
							required
							fullWidth
							id="email"
							label={i18n.t("login.form.email")}
							name="email"
							value={user.email}
							onChange={handleChangeInput}
							autoComplete="email"
							autoFocus
						/>
						<TextField
							variant="outlined"
							margin="normal"
							required
							fullWidth
							name="password"
							label={i18n.t("login.form.password")}
							type="password"
							id="password"
							value={user.password}
							onChange={handleChangeInput}
							autoComplete="current-password"
						/>
						<Button type="submit" fullWidth variant="contained" color="primary" className={classes.submit}>
							{i18n.t("login.buttons.submit")}
						</Button>
						{renderSignupLink()}
					</form>
				</div>
			</Container>
		</div>
	);

	const renderModel2 = () => (
		<div className={classes.model2Root}>
			<CssBaseline />
			<div className={classes.model2Shell}>
				<section className={classes.model2BrandPanel}>
					<div className={classes.model2BrandContent}>
						<div className={classes.model2LogoWrap}>
							<img className={classes.model2Logo} alt="logo" />
						</div>
						<p className={classes.model2Eyebrow}>Atendimento inteligente</p>
						<h1 className={classes.model2Title}>Centralize conversas, filas e automações.</h1>
						<p className={classes.model2Subtitle}>
							Entre no CChat para acompanhar atendimentos, organizar sua equipe e responder clientes com mais velocidade.
						</p>
					</div>

					<div className={classes.model2TrustList}>
						<div className={classes.model2TrustItem}>
							<FlashOnIcon />
							<span>Operação mais rápida no WhatsApp</span>
						</div>
						<div className={classes.model2TrustItem}>
							<ForumIcon />
							<span>Filas e conversas em um só lugar</span>
						</div>
						<div className={classes.model2TrustItem}>
							<SecurityIcon />
							<span>Acesso seguro para sua empresa</span>
						</div>
					</div>

					<img className={classes.model2Mascot} src={robotGuide} alt="" aria-hidden="true" />
				</section>

				<section className={classes.model2FormPanel}>
					<div className={classes.model2FormCard}>
						<div className={classes.model2FormLogoBox}>
							<img
								className={classes.model2WhiteLogo}
								src="/imgs/site/Cchat_imgs/cchat-logo-white-transparent.png"
								alt="CChat"
							/>
						</div>
						<div className={classes.model2FormHeader}>
							<h2 className={classes.model2FormTitle}>Bem-vindo ao CChat</h2>
							<p className={classes.model2FormSubtitle}>
								Acesse sua conta para gerenciar atendimentos, equipes e automações.
							</p>
						</div>

						<form className={classes.form} noValidate onSubmit={handlSubmit}>
							<TextField
								className={classes.model2TextField}
								variant="outlined"
								required
								fullWidth
								id="email"
								label={i18n.t("login.form.email")}
								name="email"
								value={user.email}
								onChange={handleChangeInput}
								autoComplete="email"
								autoFocus
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<EmailOutlinedIcon />
										</InputAdornment>
									),
								}}
							/>
							<TextField
								className={classes.model2TextField}
								variant="outlined"
								required
								fullWidth
								name="password"
								label={i18n.t("login.form.password")}
								type={showPassword ? "text" : "password"}
								id="password"
								value={user.password}
								onChange={handleChangeInput}
								autoComplete="current-password"
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<LockOutlinedIcon />
										</InputAdornment>
									),
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												className={classes.model2PasswordToggle}
												aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
												onClick={handleTogglePassword}
												onMouseDown={(event) => event.preventDefault()}
											>
												{showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
							<Button type="submit" fullWidth variant="contained" className={classes.model2Submit}>
								{i18n.t("login.buttons.submit")}
							</Button>
							<div className={classes.model2Links}>
								{renderSignupLink()}
							</div>
						</form>
					</div>
				</section>
			</div>
		</div>
	);

	return (
		<>
			<Helmet>
				<title>{appName || "Cchat"}</title>
				<link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
			</Helmet>
			{loginPageModel === "model2" ? renderModel2() : renderModel1()}
		</>
	);
};

export default Login;
